import { useCallback, useLayoutEffect } from 'react';

import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch, useSelector } from 'react-redux';

import Node from '@/components/organisms/Dialogs/Node/Node';
import NodePlaceholder from '@/components/organisms/Dialogs/Node/Placeholder/Placeholder';
import ToolkitControls from '@/components/organisms/Dialogs/Node/ToolkitControls/ToolkitControls';
import { useCopyPaste } from '@/hooks/useCopyPaste';
import { useInfiniteCanvas } from '@/hooks/useInfiniteCanvas';
import { cn } from '@/lib/utils';
import { isModalOpen } from '@/redux/modals/selectors';
import { clearSelection } from '@/redux/nodes/actions';
import { selectHasNoSelection } from '@/redux/nodes/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { isKeyEnter, preventClickThrough } from '@/util/util';

import './DialogEditor.scss';

const DialogEditor = () => {
  const dispatch = useDispatch();
  const { handlePaste } = useCopyPaste();
  const modalOpen = useSelector(isModalOpen);
  const {
    viewportRef,
    contentRef,
    zoomPercentage,
    onZoomInClick,
    onZoomOutClick,
    onFitClick,
    frameInitial,
    consumePan,
  } = useInfiniteCanvas();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const rootId = useSelector((state: RootState) => state.nodes.nodes[0]?.node_id);
  const hasNoSelection = useSelector(selectHasNoSelection);

  useHotkeys('mod+v', () => handlePaste(), {
    enabled: canWrite && hasNoSelection && !modalOpen,
    enableOnFormTags: false,
  });

  // Top-align a populated graph on open; centre the empty-state placeholder.
  useLayoutEffect(() => {
    frameInitial(Boolean(rootId));
  }, [rootId, frameInitial]);

  const onClick = useCallback(
    (e) => {
      preventClickThrough(e);
      // A drag-pan ends in a click; don't let it clear the selection.
      if (consumePan()) {
        return;
      }
      dispatch(clearSelection());
    },
    [consumePan, dispatch]
  );

  const onKeyUp = useCallback(
    (e) => {
      preventClickThrough(e);
      if (isKeyEnter(e)) {
        dispatch(clearSelection());
      }
    },
    [dispatch]
  );

  const containerClassNames = cn({
    dialog_editor__container: true,
    dialog_editor__readOnly: !canWrite,
  });

  const renderComponent = () => {
    if (rootId) {
      return <Node nodeId={rootId} />;
    } else {
      return <NodePlaceholder show parentId={rootId} />;
    }
  };

  return (
    <div
      ref={viewportRef}
      role="presentation"
      className={containerClassNames}
      onKeyUp={onKeyUp}
      onClick={onClick}
    >
      <div ref={contentRef} className="dialog_editor__canvas">
        {renderComponent()}
      </div>

      <ToolkitControls
        onZoomInClick={onZoomInClick}
        onZoomOutClick={onZoomOutClick}
        onFitClick={onFitClick}
        zoomPercentage={zoomPercentage}
      />
    </div>
  );
};

export default DialogEditor;
